import React, { createContext, useContext, useState, useCallback } from 'react';

const RetroContext = createContext();

export const useRetroContext = () => {
    const context = useContext(RetroContext);
    if (!context) {
        throw new Error('useRetroContext must be used within a RetroProvider');
    }
    return context;
};

// Safe version that returns null/no-ops when outside RetroProvider
export const useRetroContextSafe = () => {
    const context = useContext(RetroContext);
    if (!context) {
        return {
            currentApp: null,
            currentProject: null,
            currentScenario: null,
            appConfig: null,
            isTransitioning: false,
            transitionToApp: () => {},
            switchAppOnly: () => {},
            setProjectContext: () => {},
            scenarioTab: 0,
            setScenarioTab: () => {},
            phdSection: 'dashboard',
            setPhdSection: () => {},
            evSection: 'site',
            setEvSection: () => {},
            evProjectType: null,
            setEvProjectType: () => {},
            evScenarioTab: 0,
            setEvScenarioTab: () => {},
            coldSection: 'zones',
            setColdSection: () => {},
            APP_CONFIGS: {},
            scenarioValidation: { requiredFields: {}, missingFields: [] },
            setScenarioValidation: () => {},
            activeProject: null,
            setActiveProject: () => {},
            clearProjectScope: () => {},
        };
    }
    return context;
};

// App configurations for contextual sidebar content
const APP_CONFIGS = {
    dashboard: {
        id: 'dashboard',
        name: 'Mission Control',
        color: '#b28c50',
        statusLabel: 'System Overview',
        tips: [
            "Use Ctrl+N to quickly create a new project",
            "Drag the globe to rotate it manually",
            "Click the oscilloscope to send a ping",
        ],
    },
    'site-dna': {
        id: 'site-dna',
        name: 'Site DNA',
        color: '#b28c50',
        statusLabel: 'Project Analysis',
        tips: [
            "Batteries under 2hr duration rarely pencil for demand charge savings",
            "NEM 3.0 projects benefit from west-facing arrays",
            "Load factor below 40% indicates strong demand charge savings potential",
        ],
    },
    'flux-ems': {
        id: 'flux-ems',
        name: 'Flux EMS',
        color: '#3f6a64',
        statusLabel: 'System Monitoring',
        tips: [
            "Monitor real-time power flow for optimization opportunities",
            "Check battery state of charge before peak hours",
            "Review dispatch schedules for demand charge management",
        ],
    },
    'flux-phd': {
        id: 'flux-phd',
        name: 'Flux PhD',
        color: '#9b59b6',
        statusLabel: 'Topology Simulation',
        tips: [
            "1-minute resolution reveals clipping losses missed by hourly models",
            "DC-coupled systems show 3-8% efficiency gains in topology analysis",
            "Physics-based models quantify real-world efficiency advantages",
        ],
    },
    'flux-ev': {
        id: 'flux-ev',
        name: 'Flux EV',
        color: '#4CAF50',
        statusLabel: 'EV Charging Modeling',
        tips: [
            "Overnight charging typically costs 30-50% less than daytime charging",
            "Level 2 chargers are most cost-effective for depot charging",
            "Demand charges often exceed energy costs for fleet charging",
        ],
    },
    'flux-cold': {
        id: 'flux-cold',
        name: 'Flux COLD',
        color: '#2196F3',
        statusLabel: 'Cold Storage Optimization',
        tips: [
            "Refrigeration loads are highly flexible - ideal for demand response",
            "Thermal mass allows load shifting without compromising product quality",
            "Peak demand charges often dominate cold storage energy costs",
        ],
    },
    admin: {
        id: 'admin',
        name: 'Admin Panel',
        color: '#e74c3c',
        statusLabel: 'System Administration',
        tips: [
            "Review pending tariffs regularly for accuracy",
            "Monitor solver comparison results for drift",
            "Check API key status in system health",
        ],
    },
};

export function RetroProvider({ children }) {
    const [currentApp, setCurrentApp] = useState('dashboard');
    const [currentProject, setCurrentProject] = useState(null);
    const [currentScenario, setCurrentScenario] = useState(null);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [scenarioTab, setScenarioTab] = useState(0);
    const [phdSection, setPhdSection] = useState('dashboard');
    const [evSection, setEvSection] = useState(null);  // No default - FluxEV.js calculates based on project type
    const [evProjectType, setEvProjectType] = useState(null);  // 'fleet' | 'depot' | null
    const [evScenarioTab, setEvScenarioTab] = useState(0);
    const [coldSection, setColdSection] = useState('zones');
    // Scenario input validation state - updated by ScenarioEditor
    const [scenarioValidation, setScenarioValidation] = useState({
        requiredFields: {},
        missingFields: [],
    });

    // Active project scope — persists across app transitions (unlike currentProject)
    const [activeProject, setActiveProjectRaw] = useState(() => {
        try {
            const stored = sessionStorage.getItem('vortx_active_project');
            return stored ? JSON.parse(stored) : null;
        } catch { return null; }
    });

    const setActiveProject = useCallback((project) => {
        setActiveProjectRaw(project);
        if (project) {
            sessionStorage.setItem('vortx_active_project', JSON.stringify(project));
        } else {
            sessionStorage.removeItem('vortx_active_project');
        }
    }, []);

    const clearProjectScope = useCallback(() => {
        setActiveProject(null);
    }, [setActiveProject]);

    // Transition to a new app with animation
    const transitionToApp = useCallback((appId) => {
        if (appId === currentApp) return;

        setIsTransitioning(true);

        // Short delay for fade-out, then switch content
        setTimeout(() => {
            setCurrentApp(appId);
            setCurrentProject(null);
            setCurrentScenario(null);
            setScenarioTab(0); // Reset scenario tab when changing apps

            // Allow fade-in
            setTimeout(() => {
                setIsTransitioning(false);
            }, 50);
        }, 200);
    }, [currentApp]);

    // Switch app without clearing project/scenario — used when activeProject is set
    const switchAppOnly = useCallback((appId) => {
        if (appId === currentApp) return;
        setIsTransitioning(true);
        setTimeout(() => {
            setCurrentApp(appId);
            // Intentionally DO NOT clear currentProject/currentScenario
            setTimeout(() => setIsTransitioning(false), 50);
        }, 200);
    }, [currentApp]);

    // Set current project context - updates instantly without fade transition
    // Only app transitions (dashboard → site-dna) should trigger fade animations
    const setProjectContext = useCallback((project, scenario = null) => {
        setCurrentProject(project);
        setCurrentScenario(scenario);
    }, []);

    const appConfig = APP_CONFIGS[currentApp] || APP_CONFIGS.dashboard;

    const value = {
        currentApp,
        currentProject,
        currentScenario,
        appConfig,
        isTransitioning,
        transitionToApp,
        switchAppOnly,
        setProjectContext,
        scenarioTab,
        setScenarioTab,
        phdSection,
        setPhdSection,
        evSection,
        setEvSection,
        evProjectType,
        setEvProjectType,
        evScenarioTab,
        setEvScenarioTab,
        coldSection,
        setColdSection,
        APP_CONFIGS,
        scenarioValidation,
        setScenarioValidation,
        activeProject,
        setActiveProject,
        clearProjectScope,
    };

    return (
        <RetroContext.Provider value={value}>
            {children}
        </RetroContext.Provider>
    );
}

export default RetroContext;
